import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useSelector } from "react-redux";

const COLORS = {
  male: "url(#blueGradient)",
  female: "url(#pinkGradient)",
  other: "url(#purpleGradient)",
  na: "url(#neutralGradient)",
};

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-medium"
    >
      {`${name}\n${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-100 text-sm">
        <p className="font-semibold text-gray-800">{payload[0].name}</p>
        <p className="text-gray-600">
          Count: <span className="font-medium">{payload[0].value.toLocaleString()}</span>
        </p>
        <p className="text-gray-600">
          Percentage:{" "}
          <span className="font-medium">
            {(payload[0].payload.percent * 100).toFixed(1)}%
          </span>
        </p>
      </div>
    );
  }
  return null;
};

const GenderChart = ({ type, states }) => {

  const pid = useSelector((state) => state.user.projectId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {

    const fetchData = async () => {
      try {

        setLoading(true);

        let url = `https://api-project360-insights.haqdarshak.com/kpi/citizen/gender-${type}/?pid=${pid}`;

        states?.forEach((s) => {
          url += `&State=${s.value}`;
        });

        const response = await fetch(url);

        if (!response.ok) throw new Error("Network response was not ok");

        const json = await response.json();

        const transformed = {
          male: {
            count: json.male_count || 0,
            percentage: json.male_percentage || 0,
          },
          female: {
            count: json.female_count || 0,
            percentage: json.female_percentage || 0,
          },
          other: {
            count: json.other_count || 0,
            percentage: json.other_percentage || 0,
          },
          not_available: {
            count: json.not_available_count || 0,
            percentage: json.not_available_percentage || 0,
          },
          total: json.total_count || 0,
        };

        setData(transformed);

      } catch (err) {

        console.error("Error fetching gender data:", err);
        setError(err.message);

      } finally {
        setLoading(false);
      }
    };

    if (pid) fetchData();

  }, [pid, type, states]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );

  if (error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
        Error loading gender data: {error}
      </div>
    );

  if (!data) return null;

  const total = data.total || 1;

  const pieData = [
    { name: "Male", value: data.male.count, percent: data.male.count / total },
    { name: "Female", value: data.female.count, percent: data.female.count / total },
  ];

  const cardData = [
    { name: "Male", value: data.male.count, percent: data.male.count / total, color: "#6366F1" },
    { name: "Female", value: data.female.count, percent: data.female.count / total, color: "#EC4899" },
    { name: "Other", value: data.other.count, percent: data.other.count / total, color: "#8B5CF6" },
    {
      name: "N/A",
      value: data.not_available.count,
      percent: data.not_available.count / total,
      color: "#A1A1AA",
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

      <div className="p-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800">
          Gender Distribution
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-4 p-4">

        {/* Pie Chart */}

        <div className="h-64">

          <ResponsiveContainer width="100%" height="100%">
            <PieChart>

              <defs>
                <linearGradient id="blueGradient">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#6366F1" />
                </linearGradient>

                <linearGradient id="pinkGradient">
                  <stop offset="0%" stopColor="#F472B6" />
                  <stop offset="100%" stopColor="#EC4899" />
                </linearGradient>

                <linearGradient id="purpleGradient">
                  <stop offset="0%" stopColor="#A78BFA" />
                  <stop offset="100%" stopColor="#8B5CF6" />
                </linearGradient>

                <linearGradient id="neutralGradient">
                  <stop offset="0%" stopColor="#D1D5DB" />
                  <stop offset="100%" stopColor="#A1A1AA" />
                </linearGradient>
              </defs>

              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={110}
                paddingAngle={2}
                label={(props) => renderCustomizedLabel({ ...props })}
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.name === "Male" ? COLORS.male : COLORS.female}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>

              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="center"
                wrapperStyle={{ paddingBottom: "10px" }}
              />

              <Tooltip content={<CustomTooltip />} />

            </PieChart>
          </ResponsiveContainer>

        </div>

        {/* Table */}

        <div className="flex flex-col">

          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 mb-4">
            <p className="text-sm font-medium text-blue-600 mb-1">
              Total Participants
            </p>
            <p className="text-2xl font-bold text-blue-800">
              {data.total.toLocaleString()}
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200 shadow-xs">

            <table className="min-w-full divide-y divide-gray-200">

              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                    Gender
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-r border-gray-200">
                    Count
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Percentage
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">

                {cardData.map((item, idx) => (

                  <tr key={idx} className="hover:bg-gray-50">

                    <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200">

                      <div className="flex items-center">

                        <div
                          className="w-2.5 h-2.5 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        ></div>

                        {item.name}

                      </div>

                    </td>

                    <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                      {item.value.toLocaleString()}
                    </td>

                    <td className="px-4 py-3 text-sm text-gray-700">
                      {(item.percent * 100).toFixed(1)}%
                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>

      </div>

    </div>
  );
};

export default GenderChart;